import React, { useEffect, useState } from 'react';
import { Modal, Button, ConfigProvider } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getUnseenFeatureAnnouncementsQuery } from '../../util/query/whatsNew.query';
import { markFeatureAnnouncementSeenMutation } from '../../util/mutation/whatsNew.mutation';
import { FeatureTourRunner } from './FeatureTourRunner';

// This app's actual brand teal (see components/navbar/navbar.css and
// Workforce360's colors.ts, both `#0D5C63`) — antd's default theme token is
// blue, which is why an un-themed Tour/Modal looks out of place here.
const BRAND_COLOR = '#0D5C63';

// Works through the unseen-announcement queue one at a time: a summary card
// (title/media/summary), then — if the announcement has tourSteps — a live
// FeatureTourRunner walkthrough. Each announcement is marked seen as soon as
// its own phase completes, not batched at the end, so closing mid-queue
// still records what was actually shown.
export function WhatsNewModal() {
  const [fetchAnnouncements, { data }] = useLazyQuery(getUnseenFeatureAnnouncementsQuery, {
    variables: { app: 'public' },
    fetchPolicy: 'network-only',
  });
  const [markSeen] = useMutation(markFeatureAnnouncementSeenMutation);

  useEffect(() => {
    fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const announcements = data?.getUnseenFeatureAnnouncements?.response || [];
  const [queueIndex, setQueueIndex] = useState(0);
  const [phase, setPhase] = useState('summary'); // 'summary' | 'tour'

  const current = announcements[queueIndex];

  const advanceQueue = (tourCompleted) => {
    if (current?._id) {
      markSeen({ variables: { announcementId: current._id, tourCompleted } });
    }
    setPhase('summary');
    setQueueIndex((i) => i + 1);
  };

  if (!current) return null;

  const tourSteps = current.tourSteps?.length
    ? [...current.tourSteps].sort((a, b) => a.order - b.order)
    : [];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: BRAND_COLOR } }}>
      {phase === 'summary' && (
        <Modal open closable onCancel={() => advanceQueue(false)} footer={null} centered>
          <div style={{padding:'15px'}}>
            {current.media && (
              <img
                src={current.media}
                alt=""
                style={{ width: '100%', maxHeight: 220, objectFit: 'contain', marginBottom: 16, borderRadius: 8 }}
              />
            )}
            <h2 style={{ marginBottom: 8 }}>{current.title}</h2>
            <p style={{ color: '#595959', marginBottom: 20 }}>{current.summary}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button onClick={() => advanceQueue(false)}>Skip</Button>
              {tourSteps.length > 0 && (
                <Button type="primary" onClick={() => setPhase('tour')}>Take the tour</Button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {phase === 'tour' && tourSteps.length > 0 && (
        <FeatureTourRunner steps={tourSteps} onDone={advanceQueue} cleanupSelector={current.cleanupSelector} />
      )}
    </ConfigProvider>
  );
}
